<template>
  <div>
    <b-card>
      <TitleTable titleTable="GLP-1 Prices" iconTable="fas fa-dollar-sign" />
      <p class="text-muted px-2 mb-2 small">
        Prices are <strong>per dose (unit price)</strong> and append-only.
        Leave facility empty to apply to <strong>all facilities</strong>.
        To change a price, add a new record with a later effective date.
      </p>
      <b-row class="m-1 mb-2">
        <b-col cols="12" class="d-flex justify-content-end">
          <GoBack class="mr-2" />
          <b-button variant="primary" @click="openAdd">Add price</b-button>
        </b-col>
      </b-row>
      <b-table :items="items" :fields="fields" bordered responsive show-empty empty-text="No prices recorded">
        <template #cell(facility_id)="data">
          {{ facilityLabel(data.value) }}
        </template>
        <template #cell(weightloss_treatment_id)="data">
          {{ treatmentLabel(data.value) }}
        </template>
        <template #cell(weightloss_dose_id)="data">
          {{ doseLabel(data.value, data.item) }}
        </template>
        <template #cell(effective_date)="data">
          {{ formatProjectDateDisplay(data.value) }}
        </template>
        <template #cell(actions)="data">
          <b-button
            size="sm"
            variant="outline-primary"
            v-b-tooltip.hover
            title="Add new price version (append-only)"
            @click="openNewVersion(data.item)">
            <i class="fas fa-plus"></i>
          </b-button>
        </template>
      </b-table>
    </b-card>
    <WlFormSidebar
      :visible.sync="sidebarVisible"
      sidebar-id="wl-price-sidebar"
      title="New unit price"
      :loading="saving"
      @submit="save"
      @close="onSidebarClose">
      <b-form-group label="Treatment *">
        <treeselect
          v-model="form.weightloss_treatment_id"
          :options="treatmentOptions"
          :clearable="false"
          placeholder="Select treatment"
          @input="onTreatmentChange" />
      </b-form-group>
      <b-form-group label="Dose *">
        <treeselect
          v-model="form.weightloss_dose_id"
          :options="doseOptions"
          :clearable="false"
          :disabled="!form.weightloss_treatment_id || loadingDoses"
          placeholder="Select dose" />
        <small class="text-muted d-block mt-1">
          Unit price for 1 dose of this strength.
        </small>
      </b-form-group>
      <b-form-group label="Facility">
        <treeselect
          v-model="form.facility_id"
          :options="facilityOptions"
          :normalizer="normalizer"
          :clearable="true"
          placeholder="All facilities"
          :disabled="facilityOptionsLoading || saving" />
        <small class="text-muted d-block mt-1">
          Empty = applies to all facilities. Set a facility only for a site-specific override.
        </small>
      </b-form-group>
      <b-form-group label="Unit price (1 dose) *">
        <b-form-input v-model="form.price" type="number" step="0.01" min="0" />
      </b-form-group>
      <b-form-group label="Effective from *">
        <flat-pickr
          v-model="form.effective_date"
          :config="dateConfig"
          class="form-control"
          placeholder="MM-DD-YYYY"
          @on-change="onEffectiveDateChange" />
        <small class="text-muted d-block mt-1">Format: MM-DD-YYYY (e.g. 07-15-2026)</small>
      </b-form-group>
      <b-form-group label="Notes">
        <b-form-input v-model="form.notes" placeholder="Optional" />
      </b-form-group>
    </WlFormSidebar>
    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BButton,
  BFormGroup,
  BFormInput,
  VBTooltip,
} from 'bootstrap-vue'
import Treeselect from '@riophae/vue-treeselect'
import flatPickr from 'vue-flatpickr-component'
import TitleTable from '@/components/TitleTable.vue'
import GoBack from '@/components/ButtonBack.vue'
import ToastNotification from '@/components/ToastNotification'
import WlFormSidebar from '../components/WlFormSidebar.vue'
import wlPricesApi from '@/core/services/api/weightloss-inventory/prices'
import wlTreatmentsApi from '@/core/services/api/weightloss-inventory/treatments'
import wlDosesApi from '@/core/services/api/weightloss-inventory/doses'
import {
  todayProjectDate,
  toProjectDate,
  formatProjectDateDisplay,
} from '@/core/services/api/weightloss-inventory/dateFormat'
import axiosF from '@/core/services/api/admin/facilities'

function emptyForm() {
  return {
    weightloss_treatment_id: null,
    weightloss_dose_id: null,
    facility_id: null,
    price: '',
    effective_date: todayProjectDate(),
    notes: '',
  }
}

export default {
  name: 'TableWlPrices',
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BFormGroup,
    BFormInput,
    Treeselect,
    flatPickr,
    TitleTable,
    GoBack,
    ToastNotification,
    WlFormSidebar,
  },
  directives: { 'b-tooltip': VBTooltip },
  data() {
    return {
      normalizer(node) {
        return { id: node.id, label: node.name }
      },
      dateConfig: {
        dateFormat: 'm-d-Y',
        allowInput: true,
        altInput: false,
      },
      items: [],
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'weightloss_treatment_id', label: 'Treatment' },
        { key: 'weightloss_dose_id', label: 'Dose' },
        { key: 'facility_id', label: 'Facility' },
        { key: 'price', label: 'Unit price' },
        { key: 'effective_date', label: 'Effective from' },
        { key: 'actions', label: '' },
      ],
      treatmentOptions: [],
      treatmentsRaw: [],
      doseOptions: [],
      dosesCache: {},
      facilityOptions: [],
      facilityOptionsLoading: false,
      loadingDoses: false,
      sidebarVisible: false,
      saving: false,
      form: emptyForm(),
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    formatProjectDateDisplay,
    async init() {
      this.treatmentsRaw = await wlTreatmentsApi.list()
      this.treatmentOptions = (this.treatmentsRaw || []).map(t => ({
        id: t.id,
        label: t.name,
      }))
      await this.loadFacilities()
      this.load()
    },
    async loadFacilities() {
      this.facilityOptionsLoading = true
      try {
        const { data } = await axiosF.facilityList(1000)
        this.facilityOptions = (data || []).map(item => ({
          id: item.id,
          name: item.name,
        }))
      } catch (e) {
        this.facilityOptions = []
        this.$refs.toast?.danger(e?.message || 'Could not load facilities')
      } finally {
        this.facilityOptionsLoading = false
      }
    },
    facilityLabel(id) {
      if (id == null || id === '') return 'All facilities'
      const f = this.facilityOptions.find(x => x.id === id || x.id === Number(id))
      return f ? f.name : `#${id}`
    },
    treatmentLabel(id) {
      if (id == null || id === '') return '—'
      const t = this.treatmentOptions.find(x => x.id === id || x.id === Number(id))
      return t ? t.label : `#${id}`
    },
    doseLabel(id, item) {
      if (item?.dose?.dose_label) return item.dose.dose_label
      if (id == null || id === '') return '—'
      const cached = Object.values(this.dosesCache)
        .flat()
        .find(d => d.id === id || d.id === Number(id))
      return cached ? cached.dose_label : `#${id}`
    },
    onSidebarClose() {
      this.form = emptyForm()
      this.doseOptions = []
    },
    openAdd() {
      this.form = emptyForm()
      this.doseOptions = []
      this.sidebarVisible = true
    },
    openNewVersion(item) {
      this.form = {
        weightloss_treatment_id: item.weightloss_treatment_id,
        weightloss_dose_id: item.weightloss_dose_id,
        facility_id: item.facility_id != null ? Number(item.facility_id) : null,
        price: item.price,
        effective_date: todayProjectDate(),
        notes: '',
      }
      this.loadDosesForTreatment(item.weightloss_treatment_id).then(() => {
        this.sidebarVisible = true
      })
    },
    onTreatmentChange() {
      this.form.weightloss_dose_id = null
      this.loadDosesForTreatment(this.form.weightloss_treatment_id)
    },
    async loadDosesForTreatment(treatmentId) {
      if (!treatmentId) {
        this.doseOptions = []
        return
      }
      this.loadingDoses = true
      try {
        let doses = this.dosesCache[treatmentId]
        if (!doses) {
          doses = await wlDosesApi.list({
            weightloss_treatment_id: treatmentId,
            active: true,
          })
          this.$set(this.dosesCache, treatmentId, doses || [])
        }
        this.doseOptions = (doses || []).map(d => ({
          id: d.id,
          label: d.dose_label,
        }))
      } catch {
        this.doseOptions = []
      } finally {
        this.loadingDoses = false
      }
    },
    onEffectiveDateChange(selectedDates, dateStr) {
      // Forzar siempre string MM-DD-YYYY (flatpickr a veces deja Date / ISO en el modelo)
      const raw = dateStr || (selectedDates && selectedDates[0]) || this.form.effective_date
      this.form.effective_date = toProjectDate(raw) || ''
    },
    async load() {
      try {
        this.items = await wlPricesApi.list()
      } catch (e) {
        this.$refs.toast.danger(e.message)
      }
    },
    async save() {
      if (!this.form.weightloss_treatment_id) {
        this.$refs.toast.danger('Treatment is required')
        return
      }
      if (!this.form.weightloss_dose_id) {
        this.$refs.toast.danger('Dose is required')
        return
      }
      if (this.form.price === '' || this.form.price == null) {
        this.$refs.toast.danger('Unit price is required')
        return
      }
      // Normalizar justo antes de enviar: el backend exige exactamente MM-DD-YYYY
      const effective = toProjectDate(this.form.effective_date)
      if (!effective || !/^\d{2}-\d{2}-\d{4}$/.test(effective)) {
        this.$refs.toast.danger('Effective date is required in MM-DD-YYYY format (e.g. 07-15-2026)')
        return
      }
      this.form.effective_date = effective

      const payload = {
        weightloss_treatment_id: Number(this.form.weightloss_treatment_id),
        weightloss_dose_id: Number(this.form.weightloss_dose_id),
        facility_id:
          this.form.facility_id != null && this.form.facility_id !== ''
            ? Number(this.form.facility_id)
            : null,
        price: Number(this.form.price),
        effective_date: effective,
      }
      const notes = (this.form.notes || '').trim()
      if (notes) payload.notes = notes

      this.saving = true
      try {
        await wlPricesApi.create(payload)
        this.$refs.toast.success('Price recorded')
        this.sidebarVisible = false
        this.load()
      } catch (e) {
        this.$refs.toast.danger(e.message)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
